import React, { useEffect, useMemo, useRef, useState } from "react"

import Flickity from "flickity"
import { chevronLeft, chevronRight } from "../hooks/icons"
import { isMobile } from "../lib/utils"

type SiblingProduct = {
  handle: string
  imageUrl: string
}

type Props = {
  media: string[]
  instantSearch?: boolean
  modelToggle?: boolean
  productSiblingData?: SiblingProduct[]
  currentHandle: string
  hoveredSiblingHandle?: string | null
  intellisuggestData?: string
  intellisuggestSignature?: string
  siteId?: string
}

export function ProductCardMedia({
  media,
  instantSearch = false,
  modelToggle,
  currentHandle = "",
  productSiblingData = [],
  hoveredSiblingHandle = null,
  intellisuggestData,
  intellisuggestSignature,
  siteId = "gqj2zz",
}: Props) {
  const carouselRef = useRef<HTMLDivElement>(null)
  const flickityInstance = useRef<Flickity | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)

  // Utility to find index of image containing substring
  const findImageIndex = (substr: string) => {
    return media.findIndex((img) => img?.includes(substr))
  }

  const orderedMedia = useMemo(() => {
    if (!media || media.length === 0) return []

    let productIndex = findImageIndex("_2_")
    if (productIndex === -1) {
      productIndex = findImageIndex("_2.")
    }
    productIndex = productIndex !== -1 ? productIndex : 0

    let modelIndex = findImageIndex("_1_")
    if (modelIndex === -1) {
      modelIndex = findImageIndex("_1.")
    }
    modelIndex = modelIndex !== -1 ? modelIndex : 1

    if (media.length < 2) return media

    if (modelToggle) {
      // Show model photo first, product photo second
      return [
        media[modelIndex],
        media[productIndex],
        ...media.filter((_, idx) => idx !== productIndex && idx !== modelIndex),
      ]
    } else {
      // Show product photo first, model photo second
      return [
        media[productIndex],
        media[modelIndex],
        ...media.filter((_, idx) => idx !== productIndex && idx !== modelIndex),
      ]
    }
  }, [media, modelToggle])

  useEffect(() => {
    if (!carouselRef.current || typeof window === "undefined") return

    const flickity = new Flickity(carouselRef.current, {
      cellAlign: "left",
      pageDots: false,
      draggable:
        (isMobile && orderedMedia?.length > 1) ||
        (instantSearch && orderedMedia?.length > 1)
          ? true
          : false,
      adaptiveHeight: true,
      contain: true,
      imagesLoaded: true,
      prevNextButtons: false,
    })

    flickityInstance.current = flickity

    flickity.on("change", (index: number) => {
      setCurrentIndex(index)
    })

    return () => {
      flickity.destroy()
    }
  }, [])

  if (!orderedMedia || orderedMedia.length === 0) return null

  const handlePrev = () => flickityInstance.current?.previous()
  const handleNext = () => flickityInstance.current?.next()

  const progress = ((currentIndex + 1) / media.length) * 100

  return (
    <div style={{ position: "relative" }}>
      <a
        href={`/products/${currentHandle}`}
        onMouseDown={(event) =>
          window.IntelliSuggest?.clickItem(event.currentTarget as HTMLElement, {
            siteId: siteId,
            intellisuggestData: intellisuggestData,
            intellisuggestSignature: intellisuggestSignature,
          })
        }
      >
        <div ref={carouselRef}>
          {orderedMedia.map((mediaItem, index) => (
            <img
              key={index}
              src={`${mediaItem}&width=600`}
              srcSet={`
              ${mediaItem}&width=400 400w,
              ${mediaItem}&width=600 600w
            `}
              className="product-card--photo"
              loading={index === 0 ? "eager" : "lazy"}
              style={{ height: "auto" }}
            />
          ))}
        </div>
      </a>
      {productSiblingData?.map((sibling: SiblingProduct, index: number) =>
        sibling?.handle !== currentHandle &&
        sibling?.handle === hoveredSiblingHandle ? (
          <img
            key={index}
            src={`${sibling?.images[0]?.url}&width=600`}
            srcSet={`
        ${sibling?.images[0]?.url}&width=400 400w,
        ${sibling?.images[0]?.url}&width=600 600w
      `}
            className="product-card--photo product-sibling--photos"
            loading={"lazy"}
            data-photo-handle={`${currentHandle}-${sibling?.handle}`}
            style={{ display: "block", height: "auto" }}
          />
        ) : null
      )}

      {!isMobile && !instantSearch && orderedMedia?.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="product-card__flickity-nav flickity-nav flickity-prev"
            aria-label="Previous image"
          >
            {chevronLeft}
          </button>
          <button
            onClick={handleNext}
            className="product-card__flickity-nav flickity-nav flickity-next"
            aria-label="Next image"
          >
            {chevronRight}
          </button>
        </>
      )}
      {isMobile && orderedMedia?.length > 1 ? (
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>
      ) : null}
    </div>
  )
}
